import React from "react";
import { Play } from "lucide-react";
import { Handle, Position, NodeProps } from "reactflow";
import { handlerStyle } from "../common/commonData";
import { useNodeContextMenu } from "../common/NodeContextMenu";
import CollapsedNodeView from "../common/NodeHeaderView";

interface StartNodeData {
  label?: string;
  header?: string;
  uid?: string;
  title?: string;
  description?: string;
  onDelete?: (id: string) => void;
  borderColor?: string;
  shadowColor?: string;
}

const StartNode: React.FC<NodeProps<StartNodeData>> = ({
  id,
  data,
  isConnectable,
}) => {
  const label = data.label || data.header || "Start";
  const [isExpanded, setIsExpanded] = React.useState(false);
  const isExecutionLoading =
    (data as any).executionStatus === "running" ||
    (data as any).executionStatus === "pending";
  const [showLoadingBorder, setShowLoadingBorder] = React.useState(false);
  const loadingBorderColor = data.borderColor || "var(--primary)";

  // Context menu
  const { handleContextMenu } = useNodeContextMenu(id);

  React.useEffect(() => {
    if (isExecutionLoading && !isExpanded) {
      setShowLoadingBorder(true);
      return;
    }

    if (isExpanded) {
      setShowLoadingBorder(false);
      return;
    }

    const timer = setTimeout(() => setShowLoadingBorder(false), 350);
    return () => clearTimeout(timer);
  }, [isExecutionLoading, isExpanded]);

  return (
    <div
      style={{
        border: showLoadingBorder
          ? "1px solid var(--light-gray)"
          : `1px solid ${data.borderColor}`,
        boxShadow: showLoadingBorder ? "none" : `0 0 20px ${data.shadowColor}`,
      }}
      className={` !bg-background relative border shadow-lg p-2 text-center font-semibold transition-all ${
        isExpanded
          ? "min-w-60 max-w-80 p-4 !rounded-l-2xl !rounded-r-2xl"
          : "w-28 h-28 flex items-center justify-center !rounded-l-2xl !rounded-r-[50px]"
      }`}
      onContextMenu={handleContextMenu}
    >
      {showLoadingBorder && (
        <>
          <style>
            {`
              @keyframes start-node-spin-border {
                to {
                  transform: rotate(360deg);
                }
              }

              .start-node-loading-border {
                position: absolute;
                inset: -1px;
                border-radius: inherit;
                overflow: hidden;
                pointer-events: none;
                z-index: 0;
              }

              .start-node-loading-border::before {
                content: '';
                position: absolute;
                inset: -50%;
                background: conic-gradient(
                  from 0deg,
                  transparent 0deg,
                  transparent 300deg,
                  ${loadingBorderColor} 340deg,
                  ${loadingBorderColor} 360deg
                );
                animation: start-node-spin-border 2s linear infinite;
              }

              .start-node-loading-border::after {
                content: '';
                position: absolute;
                inset: 2px;
                background: var(--background);
                border-radius: inherit;
              }
            `}
          </style>
          <div className="start-node-loading-border" />
        </>
      )}

      <div
        className={`relative z-10 flex items-center justify-between gap-4 transition-all duration-500 ${
          isExpanded ? "p-3 rounded-2xl" : "p-3 rounded-full"
        }`}
      >
        <CollapsedNodeView
          icon={
            <Play
              className={
                (data as any).executionStatus === "running" ||
                (data as any).executionStatus === "pending"
                  ? "animate-bounce-horizontal"
                  : ""
              }
            />
          }
          label={label}
          title={data.title}
          iconSize={35}
          borderColor={data.borderColor}
          onToggle={() => setIsExpanded((prev) => !prev)}
          isExpanded={isExpanded}
          executionStatus={(data as any).executionStatus}
          variant="start"
          description={"Work Flow Triggering Point"}
        />
      </div>

      {/* Expanded content - show output data */}
      {isExpanded && (
        <>
          <div className="text-xs pt-2 border-t w-full text-muted-foreground mb-1 font-mono">
            Start Node : Output Data
          </div>
          <div className="mt-3 p-2 rounded-md text-left bg-muted">
            <pre className="text-xs text-primaryGreen break-all font-mono">
              {JSON.stringify([{}], null, 2)}
            </pre>
          </div>
        </>
      )}

      {/* Right handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        isConnectable={isConnectable}
        className={`${handlerStyle} !cursor-crosshair !z-20`}
        style={{ cursor: "crosshair", right: "-8px" }}
      />
    </div>
  );
};

export default StartNode;
